import { useCallback, useMemo, useState } from "react";
import { useMonthlyEnrollments } from "@/resources/policies/hooks/useMonthlyEnrollments";
import type { CoverageMonth } from "@/resources/policies/utils/deriveCoverageMonths";
import {
  fillFromPriorMonth,
  missingCoverageMonths,
  requiredCoverageMonths,
} from "@/resources/policies/utils/enrollmentGate";
import type { AccommodationEnrollmentInput } from "@/types/policyMonthlyEnrollment";

interface DraftEntry {
  lives: number;
  /** Filled by "Copy prior month" rather than typed. */
  copied: boolean;
}

interface UseAccommodationEnrollmentGateArgs {
  policyId: string | undefined;
  effectiveStart: string | undefined;
  effectiveEnd: string | undefined;
  /** Last month the run reads (YYYY-MM-DD); omit for year-end (the whole term). */
  throughMonth?: string;
  /** Flat annual attachment: enrollment isn't read, so nothing is required or sent. */
  isFlatAttachment: boolean;
}

export interface AccommodationEnrollmentGate {
  months: CoverageMonth[];
  /** Stored enrollment overlaid with the dialog's unsaved entries. */
  livesByMonth: ReadonlyMap<string, number>;
  copiedMonths: ReadonlySet<string>;
  missing: CoverageMonth[];
  isLoading: boolean;
  /** Stored enrollment failed to load; the empty list would otherwise read as every month missing. */
  isError: boolean;
  /** Run must stay disabled: enrollment loading or failed, a required month missing, or a cell holding an invalid entry. */
  isBlocked: boolean;
  isFlatAttachment: boolean;
  canCopyPriorMonth: boolean;
  copyPriorMonth: () => void;
  /** null discards the dialog's entry for the month (back to the stored value). */
  setLives: (coverageMonth: string, lives: number | null) => void;
  /** Tracks cells showing an invalid entry (e.g. 0), which the run would not send. */
  setInvalid: (coverageMonth: string, invalid: boolean) => void;
  /** Discards every unsaved entry (dialog opened or cancelled). */
  reset: () => void;
  /** Entries in the shown months that differ from what's stored; sent with the run. */
  enrollmentsToSave: AccommodationEnrollmentInput[];
}

/**
 * State for the enrollment grid in the accommodation run dialogs. Entries
 * stay local until Run: they are sent with the run request, and the backend saves
 * them in the run's transaction, so Cancel or a failed run saves nothing.
 */
export const useAccommodationEnrollmentGate = ({
  policyId,
  effectiveStart,
  effectiveEnd,
  throughMonth,
  isFlatAttachment,
}: UseAccommodationEnrollmentGateArgs): AccommodationEnrollmentGate => {
  const { enrollments, isLoading, isError } = useMonthlyEnrollments(policyId);
  const [drafts, setDrafts] = useState<Record<string, DraftEntry>>({});
  const [invalidMonths, setInvalidMonths] = useState<ReadonlySet<string>>(new Set());

  const months = useMemo(
    () => requiredCoverageMonths(effectiveStart, effectiveEnd, throughMonth),
    [effectiveStart, effectiveEnd, throughMonth]
  );

  const stored = useMemo(
    () => new Map(enrollments.map((e) => [e.coverage_month, e.enrolled_lives] as const)),
    [enrollments]
  );

  const livesByMonth = useMemo(() => {
    const merged = new Map(stored);
    Object.entries(drafts).forEach(([month, d]) => merged.set(month, d.lives));
    return merged;
  }, [stored, drafts]);

  const copiedMonths = useMemo(() => new Set(Object.keys(drafts).filter((month) => drafts[month].copied)), [drafts]);

  const missing = useMemo(
    () => (isFlatAttachment ? [] : missingCoverageMonths(months, livesByMonth)),
    [isFlatAttachment, months, livesByMonth]
  );

  const fillable = useMemo(() => fillFromPriorMonth(months, livesByMonth), [months, livesByMonth]);

  const copyPriorMonth = useCallback(() => {
    setDrafts((prev) => {
      const next = { ...prev };
      fillable.forEach((lives, month) => {
        next[month] = { lives, copied: true };
      });
      return next;
    });
  }, [fillable]);

  const setLives = useCallback((coverageMonth: string, lives: number | null) => {
    setDrafts((prev) => {
      const next = { ...prev };
      if (lives === null) {
        delete next[coverageMonth];
      } else {
        next[coverageMonth] = { lives, copied: false };
      }
      return next;
    });
  }, []);

  const setInvalid = useCallback((coverageMonth: string, invalid: boolean) => {
    setInvalidMonths((prev) => {
      if (prev.has(coverageMonth) === invalid) return prev;
      const next = new Set(prev);
      if (invalid) next.add(coverageMonth);
      else next.delete(coverageMonth);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setDrafts({});
    setInvalidMonths(new Set());
  }, []);

  const enrollmentsToSave = useMemo(() => {
    if (isFlatAttachment) return [];
    return months
      .filter((m) => drafts[m.coverageMonth] && drafts[m.coverageMonth].lives !== stored.get(m.coverageMonth))
      .map((m) => ({ coverage_month: m.coverageMonth, enrolled_lives: drafts[m.coverageMonth].lives }));
  }, [isFlatAttachment, months, drafts, stored]);

  return {
    months,
    livesByMonth,
    copiedMonths,
    missing,
    isLoading,
    isError,
    isBlocked: !isFlatAttachment && (isLoading || isError || missing.length > 0 || invalidMonths.size > 0),
    isFlatAttachment,
    canCopyPriorMonth: !isFlatAttachment && fillable.size > 0,
    copyPriorMonth,
    setLives,
    setInvalid,
    reset,
    enrollmentsToSave,
  };
};
